import { computed, defineComponent, ref, watch, } from '@vue/composition-api';
import { Debounce } from '@/constants/debounce';
import { mdiChevronDown, mdiChevronUp, mdiClose } from '@mdi/js';
import { useToggle } from '@/composables';
import { getUid } from '@/util/common';
import { FormElement, formElementProps, } from '@/components/Inputs/_components/FormElement';
import { BaseInput, baseInputProps, } from '@/components/Inputs/_components/BaseInput';
import { SelectDropdown, } from './_components/SelectDropdown';
import { useKeyboardNavigation } from './_composables/use-keyboard-navigation';
import { useSelectOptions } from '@/components/Inputs/SelectInput/_composables/use-select-options';
import SelectInputInner from '@/components/Inputs/SelectInput/_components/SelectInputInner/SelectInputInner.vue';
import { createPopper } from '@popperjs/core';
import { DEFAULT_SELECT_LIMIT } from '@/constants/pagination';
export default defineComponent({
    name: 'SelectInput',
    components: {
        SelectInputInner,
        SelectDropdown,
        FormElement,
        BaseInput,
    },
    props: {
        id: String,
        value: null,
        options: {
            type: Array,
            default: () => [],
        },
        optionValueName: {
            type: String,
            default: 'value',
        },
        optionLabelName: {
            type: String,
            default: 'label',
        },
        url: String,
        query: {
            type: Object,
            default: () => ({}),
        },
        disableItem: Function,
        debounce: Boolean,
        delay: {
            type: Number,
            default: Debounce.DEFAULT,
        },
        autofocus: Boolean,
        multiple: Boolean,
        autocomplete: Boolean,
        placeholder: String,
        withoutIcon: Boolean,
        hideAutocomplete: Boolean,
        loading: Boolean,
        optionsDimensionsClass: String,
        dropdownPosition: String,
        appendToBody: Boolean,
        apiV2: Boolean,
        searchProperty: {
            type: String,
            default: 'q',
        },
        compact: Boolean,
        ...formElementProps,
        ...baseInputProps,
    },
    setup(props, ctx) {
        const input = ref(null);
        const isFocused = ref(false);
        const isHovered = ref(false);
        const dropdown = ref(null);
        const position = ref({ left: 0, top: 0, width: 0 });
        const popperReference = ref(null);
        const popperInstance = ref(null);
        const initializePopper = () => {
            if (dropdown.value && popperReference.value) {
                popperInstance.value = createPopper(popperReference.value.$el, dropdown.value.$el, {
                    placement: 'bottom',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 5],
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                enabled: false,
                            },
                        },
                        {
                            name: 'hide',
                            options: {
                                enabled: false,
                            },
                        },
                        {
                            name: 'matchWidth',
                            enabled: true,
                            phase: 'beforeWrite',
                            requires: ['computeStyles'],
                            fn({ state }) {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                        },
                    ],
                });
                setTimeout(() => {
                    popperInstance.value?.update();
                }, 0);
            }
        };
        const debouncedEvent = ref(null);
        const parsedId = computed(() => {
            return props.id ?? `input-${getUid()}`;
        });
        const onFocus = () => {
            ctx.emit('focus');
            isFocused.value = true;
        };
        const onBlur = () => {
            if (debouncedEvent.value) {
                debouncedEvent.value.flush();
            }
            ctx.emit('blur');
            isFocused.value = false;
        };
        const listId = `list-${getUid()}`;
        const { isOptionSelected, selectedValues, loadedOptions, parsedOptions, optionsUpdateCounter, unselectValue, clearValues, handleSelect, update, } = useSelectOptions(props, ctx);
        const isCloseIconDisplayed = computed(() => {
            return (props.clearable && isHovered.value && selectedValues.value.length > 0);
        });
        const parsedAppendIcon = computed(() => {
            if (props.withoutIcon) {
                return '';
            }
            else if (isCloseIconDisplayed.value) {
                return mdiClose;
            }
            return isOpened.value ? mdiChevronUp : mdiChevronDown;
        });
        const autocompleteSearch = ref('');
        const { state: isOpened, toggle: toggleOptionArea } = useToggle();
        const openOptionArea = () => {
            if (isOpened.value === false) {
                isOpened.value = true;
                update();
            }
        };
        const closeOptionArea = () => {
            autocompleteSearch.value = '';
            if (isOpened.value === true) {
                isOpened.value = false;
            }
        };
        const onSelect = () => {
            autocompleteSearch.value = '';
            closeOptionArea();
        };
        const { focusedOptionIndex, focusNextOption, focusPrevOption, handleEnter, } = useKeyboardNavigation({
            options: parsedOptions,
            handleSelect: (option) => handleSelect(option, onSelect),
            openOptionArea: openOptionArea,
            autocompleteValue: autocompleteSearch,
            ctx: ctx,
        });
        const recalcPosition = () => {
            if (props.appendToBody) {
                const divPosition = popperReference.value?.$el.getBoundingClientRect();
                if (divPosition) {
                    position.value = {
                        left: divPosition.left,
                        top: divPosition.bottom + 10,
                        width: divPosition.width,
                    };
                }
            }
        };
        watch(isOpened, async (newIsOpened, oldIsOpened) => {
            recalcPosition();
            if (!newIsOpened && oldIsOpened) {
                focusedOptionIndex.value = null;
            }
            if (newIsOpened) {
                popperInstance.value
                    ? popperInstance.value.update()
                    : initializePopper();
                document.addEventListener('scroll', recalcPosition, true);
                window.addEventListener('resize', recalcPosition);
            }
            else {
                document.removeEventListener('scroll', recalcPosition);
                window.removeEventListener('resize', recalcPosition);
            }
        });
        const handleClick = () => {
            if (props.disabled)
                return;
            if (props.autocomplete) {
                openOptionArea();
                input.value?.focus();
            }
            else {
                toggleOptionArea();
            }
        };
        const handleAppendIconClick = () => {
            if (isCloseIconDisplayed.value) {
                clearValues();
            }
        };
        const computedQuery = computed(() => {
            const limitProperty = props.apiV2 ? 'perPage' : 'limit';
            const query = {
                ...props.query,
                [limitProperty]: DEFAULT_SELECT_LIMIT,
            };
            if (autocompleteSearch.value.length > 0) {
                query[props.searchProperty] = autocompleteSearch.value;
            }
            return query;
        });
        const handleLoadOptions = (data) => {
            loadedOptions.value = data;
        };
        return {
            selectedValues,
            unselectValue,
            handleAppendIconClick,
            parsedOptions,
            isFocused,
            isHovered,
            parsedAppendIcon,
            parsedId,
            onFocus,
            onBlur,
            listId,
            focusedOptionIndex,
            focusNextOption,
            focusPrevOption,
            handleEnter,
            handleClick,
            isOpened,
            closeOptionArea,
            handleSelect,
            isOptionSelected,
            autocompleteSearch,
            computedQuery,
            loadedOptions,
            onSelect,
            optionsUpdateCounter,
            dropdown,
            popperReference,
            DEFAULT_SELECT_LIMIT,
            handleLoadOptions,
            position,
        };
    },
});
